import { useMemo } from 'react';

import { useShowCause } from '@givebacks/api/core';

import { useInternalToolsMenu } from '../internalTools/useInternalToolsMenu';
import { FeatureNames } from './flags.types';
import { useHasAccessToComplianceAccountCodes } from './hooks';

type AccessFeatures = FeatureNames;

const useAccessControl_Core = ({
  feature,
  causeId,
}: {
  feature: AccessFeatures;
  causeId?: string;
}) => {
  const { getToolValue, isToolsLoading } = useInternalToolsMenu();
  const { data: cause, isInitialLoading: isCauseLoading } = useShowCause(causeId);
  const { isEnabled: complianceAccountCodeAccess, isLoading: isComplianceAccessLoading } =
    useHasAccessToComplianceAccountCodes({ causeId });

  const hasComplianceAccess = useMemo(
    () => Boolean(cause?.release?.compliance?.beta),
    [cause?.release?.compliance?.beta]
  );

  const hasContactsAccess = useMemo(
    () => Boolean(cause?.release?.contacts?.beta),
    [cause?.release?.contacts?.beta]
  );

  const hasAccountCodesAccess = useMemo(() => {
    if (cause?.is_root || cause?.is_standalone) {
      return true;
    }
    return complianceAccountCodeAccess;
  }, [cause?.is_root, cause?.is_standalone, complianceAccountCodeAccess]);

  const hasTicketedEventsAccess = useMemo(
    () => Boolean(cause?.release?.ticketed_events?.beta),
    [cause?.release?.ticketed_events?.beta]
  );

  const hasPosAccess = useMemo(
    () => Boolean(cause?.release?.pos?.beta),
    [cause?.release?.pos?.beta]
  );

  const hasQuickbooksAccess = useMemo(
    () => Boolean(cause?.release?.quickbooks?.beta),
    [cause?.release?.quickbooks?.beta]
  );

  const hasFacultyAccess = useMemo(
    () => Boolean(cause?.release?.faculty_access?.beta),
    [cause?.release?.faculty_access?.beta]
  );

  const hasFeesAccess = useMemo(
    () => Boolean(cause?.release?.student_fees?.beta),
    [cause?.release?.student_fees?.beta]
  );

  const hasFeePromotionAccess = useMemo(
    () => Boolean(cause?.release?.fee_promotion?.beta),
    [cause?.release?.fee_promotion?.beta]
  );

  const hasStoreTipsAccess = useMemo(
    () => Boolean(cause?.release?.store_tips?.beta),
    [cause?.release?.store_tips?.beta]
  );

  const hasUnitDataAccess = useMemo(
    () => Boolean(cause?.release?.unit_data?.beta),
    [cause?.release?.unit_data?.beta]
  );

  const hasDirectoryAccess = useMemo(
    () => Boolean(cause?.release?.directory?.beta),
    [cause?.release?.directory?.beta]
  );

  const hasDuesRemittanceAccess = useMemo(
    () => Boolean(cause?.release?.dues_remittance?.beta),
    [cause?.release?.dues_remittance?.beta]
  );

  const hasProductVariantsAccess = useMemo(
    () => Boolean(cause?.release?.product_variants?.beta),
    [cause?.release?.product_variants?.beta]
  );
  const hasSalesTaxBetaAccess = useMemo(
    () => Boolean(cause?.release?.sales_tax?.beta),
    [cause?.release?.sales_tax?.beta]
  );

  const status = useMemo(() => {
    // If we are in a non-production environment and the feature flag override is enabled, return true
    if (import.meta.env.VITE_API_ENV !== 'production' && getToolValue('feature-flag-override')) {
      return true;
    }
    switch (feature) {
      case 'accountCodes':
        return hasAccountCodesAccess;
      case 'compliance.beta':
        return hasComplianceAccess;
      case 'contacts.beta':
        return hasContactsAccess;
      case 'ticketedEvents.beta':
        return hasTicketedEventsAccess;
      case 'pos.beta':
        return hasPosAccess;
      case 'quickbooks':
        return hasQuickbooksAccess;
      case 'student_fees.beta':
        return hasFeesAccess;
      case 'store_tips':
        return hasStoreTipsAccess;
      case 'unit_data.beta':
        return hasUnitDataAccess;
      case 'directory.beta':
        return hasDirectoryAccess;
      case 'dues_remittance.beta':
        return hasDuesRemittanceAccess;
      case 'fee_promotion':
        return hasFeePromotionAccess;
      case 'faculty_access':
        return hasFacultyAccess;
      case 'product_variants':
        return hasProductVariantsAccess;
      case 'sales_tax.beta':
        return hasSalesTaxBetaAccess;
      default:
        return false;
    }
  }, [
    getToolValue,
    feature,
    hasAccountCodesAccess,
    hasComplianceAccess,
    hasContactsAccess,
    hasTicketedEventsAccess,
    hasPosAccess,
    hasQuickbooksAccess,
    hasFeesAccess,
    hasStoreTipsAccess,
    hasUnitDataAccess,
    hasDirectoryAccess,
    hasDuesRemittanceAccess,
    hasFeePromotionAccess,
    hasFacultyAccess,
    hasProductVariantsAccess,
    hasSalesTaxBetaAccess,
  ]);

  const isLoading = useMemo(
    () => isCauseLoading || isComplianceAccessLoading || isToolsLoading,
    [isCauseLoading, isComplianceAccessLoading, isToolsLoading]
  );

  return { accessStatus: status, isLoading };
};

export { useAccessControl_Core };
